import { Box, Button, Container, Heading, ListItem, SimpleGrid, Text, UnorderedList, VStack } from "@chakra-ui/react";
import { useState } from "react";
import { FiEye, FiFileText } from "react-icons/fi";

export default function PrivacyTermsPage() {
  const [activeTab, setActiveTab] = useState("privacy");

  return (
    <Box minH="100vh" bg="gray.100" py={8}>
      <Container maxW="container.lg">
        {/* Header */}
        <VStack spacing={3} textAlign="center" mb={8}>
          <Box bg="brand.500" w="70px" h="70px" borderRadius="full" display="flex" alignItems="center" justifyContent="center" mb={2}>
            <Box w="35px" h="40px" borderRadius="8px 8px 12px 12px" border="3px solid white" position="relative">
              <Box position="absolute" top="-8px" left="50%" transform="translateX(-50%)" w="0" h="0" borderLeft="12px solid transparent" borderRight="12px solid transparent" borderBottom="12px solid white" />
            </Box>
          </Box>
          <Heading as="h1" size="xl" color="gray.800" fontWeight="bold">
            S-Liquor Delivery App
          </Heading>
          <Text color="gray.600" fontSize="md">
            Driver Privacy Policy & Terms of Service
          </Text>
        </VStack>

        {/* Tab Buttons */}
        <SimpleGrid columns={2} gap={4} mb={6} justify="center">
          <Button
            onClick={() => setActiveTab("privacy")}
            colorScheme={activeTab === "privacy" ? "brand" : "gray"}
            bg={activeTab === "privacy" ? "brand.500" : "white"}
            color={activeTab === "privacy" ? "white" : "gray.700"}
            borderRadius="md"
            fontWeight="semibold"
            _hover={{
              bg: activeTab === "privacy" ? "brand.600" : "gray.50",
            }}
            leftIcon={
              <Box as="span" fontSize="lg">
                <FiEye />
              </Box>
            }
          >
            Privacy Policy
          </Button>
          <Button
            onClick={() => setActiveTab("terms")}
            colorScheme={activeTab === "terms" ? "brand" : "gray"}
            bg={activeTab === "terms" ? "brand.500" : "white"}
            color={activeTab === "terms" ? "white" : "gray.700"}
            borderRadius="md"
            fontWeight="semibold"
            _hover={{
              bg: activeTab === "terms" ? "brand.600" : "gray.50",
            }}
            leftIcon={
              <Box as="span" fontSize="lg">
                <FiFileText />
              </Box>
            }
          >
            Terms of Service
          </Button>
        </SimpleGrid>

        {/* Content */}
        <Box bg="white" borderRadius="lg" boxShadow="md" p={8}>
          {activeTab === "privacy" ? (
            <VStack align="stretch" spacing={6}>
              <Box borderLeft="4px" borderColor="brand.500" pl={4}>
                <Text color="gray.700" lineHeight="tall">
                  This Privacy Policy describes how S-Liquor collects, uses, and protects the personal information of drivers and staff using the S-Liquor Delivery App for internal delivery management and fulfillment operations.
                </Text>
              </Box>

              <Box>
                <Text fontWeight="bold" color="gray.700" mb={2}>
                  Last Updated: 22 December 2025
                </Text>
              </Box>

              <Box>
                <Heading as="h3" size="md" mb={3} color="gray.800">
                  Introduction
                </Heading>
                <Text color="gray.700" lineHeight="tall">
                  This Privacy Policy describes how the{" "}
                  <Text as="span" fontWeight="bold">
                    S-Liquor Delivery App
                  </Text>{" "}
                  (the "App") collects, uses, and shares information. This App is strictly for internal use by authorized S-Liquor personnel and partners.
                </Text>
              </Box>

              <Box>
                <Heading as="h3" size="md" mb={3} color="gray.800">
                  Information We Collect
                </Heading>
                <Text color="gray.700" mb={3} lineHeight="tall">
                  Because this is a logistics tool, we collect information necessary for delivery operations:
                </Text>
                <UnorderedList spacing={2} pl={6} color="gray.700">
                  <ListItem>
                    <Text as="span" fontWeight="bold">
                      Precise Location Data:
                    </Text>{" "}
                    We collect background and foreground location data to provide real-time delivery tracking to the management system and to optimize routes.
                  </ListItem>
                  <ListItem>
                    <Text as="span" fontWeight="bold">
                      Device Information:
                    </Text>{" "}
                    We may collect info about the device used (model, OS version) to ensure app compatibility and security.
                  </ListItem>
                  <ListItem>
                    <Text as="span" fontWeight="bold">
                      Activity Logs:
                    </Text>{" "}
                    We track order statuses (time of pickup, time of delivery) and app interactions to monitor performance.
                  </ListItem>
                  <ListItem>
                    <Text as="span" fontWeight="bold">
                      Account Data:
                    </Text>{" "}
                    Names and IDs provided by the S-Liquor Management System.
                  </ListItem>
                </UnorderedList>
              </Box>

              <Box>
                <Heading as="h3" size="md" mb={3} color="gray.800">
                  How We Use Your Data
                </Heading>
                <Text color="gray.700" mb={3} lineHeight="tall">
                  The data collected is used solely for:
                </Text>
                <UnorderedList spacing={2} pl={6} color="gray.700">
                  <ListItem>Facilitating and verifying liquor deliveries.</ListItem>
                  <ListItem>Monitoring fleet efficiency and driver safety.</ListItem>
                  <ListItem>Ensuring the security of the S-Liquor supply chain.</ListItem>
                </UnorderedList>
              </Box>

              <Box>
                <Heading as="h3" size="md" mb={3} color="gray.800">
                  Data Sharing and Security
                </Heading>
                <UnorderedList spacing={2} pl={6} color="gray.700">
                  <ListItem>
                    <Text as="span" fontWeight="bold">
                      No Third-Party Sales:
                    </Text>{" "}
                    We do not sell your data to third parties.
                  </ListItem>
                  <ListItem>
                    <Text as="span" fontWeight="bold">
                      Internal Access:
                    </Text>{" "}
                    Data is only accessible to S-Liquor administrators via the management system.
                  </ListItem>
                  <ListItem>
                    <Text as="span" fontWeight="bold">
                      Storage:
                    </Text>{" "}
                    Data is stored securely on S-Liquor's internal servers or authorized cloud providers.
                  </ListItem>
                </UnorderedList>
              </Box>

              <Box>
                <Heading as="h3" size="md" mb={3} color="gray.800">
                  Your Rights
                </Heading>
                <Text color="gray.700" lineHeight="tall">
                  As an internal user, your data rights are governed by your employment or service agreement with S-Liquor. You may contact your HR or IT department to request data access or corrections.
                </Text>
              </Box>
            </VStack>
          ) : (
            <VStack align="stretch" spacing={6}>
              <Box>
                <Text fontWeight="bold" color="gray.700" mb={2}>
                  Last Updated: 22 December 2025
                </Text>
              </Box>

              <Box>
                <Heading as="h3" size="md" mb={3} color="gray.800">
                  1. Acceptance of Terms
                </Heading>
                <Text color="gray.700" lineHeight="tall">
                  By logging into the S-Liquor Delivery App, you agree to be bound by these Terms and Conditions. This app is a proprietary tool of S-Liquor.
                </Text>
              </Box>

              <Box>
                <Heading as="h3" size="md" mb={3} color="gray.800">
                  2. Authorized Use Only
                </Heading>
                <UnorderedList spacing={2} pl={6} color="gray.700">
                  <ListItem>You may only access the App using the credentials provided to you by the Management System.</ListItem>
                  <ListItem>Sharing your login credentials with unauthorized individuals is strictly prohibited and may result in disciplinary action.</ListItem>
                </UnorderedList>
              </Box>

              <Box>
                <Heading as="h3" size="md" mb={3} color="gray.800">
                  3. User Obligations
                </Heading>
                <Text color="gray.700" mb={3} lineHeight="tall">
                  As a user, you agree to:
                </Text>
                <UnorderedList spacing={2} pl={6} color="gray.700">
                  <ListItem>Use the App only for assigned S-Liquor delivery tasks.</ListItem>
                  <ListItem>Provide accurate status updates (e.g., marking an item "Delivered" only when it is physically handed over).</ListItem>
                  <ListItem>Maintain the confidentiality of customer data (names, addresses, phone numbers) viewed within the App.</ListItem>
                </UnorderedList>
              </Box>

              <Box>
                <Heading as="h3" size="md" mb={3} color="gray.800">
                  4. Location Tracking
                </Heading>
                <Text color="gray.700" lineHeight="tall">
                  You acknowledge and consent to the App tracking your location during your working hours or while a delivery task is active. This is a core requirement for the fulfillment of your delivery duties.
                </Text>
              </Box>

              <Box>
                <Heading as="h3" size="md" mb={3} color="gray.800">
                  5. Prohibited Conduct
                </Heading>
                <Text color="gray.700" mb={3} lineHeight="tall">
                  You may not:
                </Text>
                <UnorderedList spacing={2} pl={6} color="gray.700">
                  <ListItem>Attempt to reverse-engineer or "hack" the App.</ListItem>
                  <ListItem>Use the App for personal errands or non-S-Liquor business.</ListItem>
                  <ListItem>Use the App to harass or contact customers for non-delivery-related reasons.</ListItem>
                </UnorderedList>
              </Box>

              <Box>
                <Heading as="h3" size="md" mb={3} color="gray.800">
                  6. Termination
                </Heading>
                <Text color="gray.700" lineHeight="tall">
                  S-Liquor reserves the right to revoke your access to the App at any time, without notice, upon termination of your employment/contract or for any breach of these terms.
                </Text>
              </Box>

              <Box>
                <Heading as="h3" size="md" mb={3} color="gray.800">
                  7. Limitation of Liability
                </Heading>
                <Text color="gray.700" lineHeight="tall">
                  S-Liquor is not responsible for any damage to personal mobile devices used to run the App unless otherwise stated in your employment contract.
                </Text>
              </Box>
            </VStack>
          )}
        </Box>

        {/* Footer */}
        <Box mt={8} textAlign="center">
          <Text fontSize="sm" color="gray.600">
            © 2025 S-Liquor. All rights reserved.
          </Text>
          <Text fontSize="sm" color="gray.600" mt={1}>
            For internal S-Liquor personnel use only.
          </Text>
        </Box>
      </Container>
    </Box>
  );
}
